/**
 * @file Skeleton.jsx
 * @description Loading placeholder with shimmer animation.
 *
 * @param {'text'|'circle'|'rect'|'button'} [variant='text']
 * @param {string|number} [width] - CSS width value
 * @param {string|number} [height] - CSS height value
 * @param {string} [borderRadius] - Custom border radius
 * @param {number} [lines=1] - Number of text lines (only for variant="text")
 * @param {string} [className]
 */
import React from 'react';

/* Inject keyframes once */
let skeletonAnimInjected = false;
function injectSkeletonAnim() {
  if (skeletonAnimInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes ib-skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}`;
  document.head.appendChild(style);
  skeletonAnimInjected = true;
}

const variantDefaults = {
  text: {
    width: '100%',
    height: '1em',
    borderRadius: 'var(--radius-sm)',
  },
  circle: {
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-full)',
  },
  rect: {
    width: '100%',
    height: '120px',
    borderRadius: 'var(--radius-md)',
  },
  button: {
    width: '120px',
    height: 'var(--btn-height-md)',
    borderRadius: 'var(--radius-lg)',
  },
};

function Skeleton({
  variant = 'text',
  width,
  height,
  borderRadius,
  lines = 1,
  className = '',
  style: userStyle,
  ...rest
}) {
  React.useEffect(() => { injectSkeletonAnim(); }, []);

  const defaults = variantDefaults[variant] || variantDefaults.text;

  const baseStyle = {
    display: 'block',
    width: width || defaults.width,
    height: height || defaults.height,
    borderRadius: borderRadius || defaults.borderRadius,
    background: `linear-gradient(
      90deg,
      var(--surface-sunken) 25%,
      var(--color-gray-100) 37%,
      var(--surface-sunken) 63%
    )`,
    backgroundSize: '200% 100%',
    animation: 'ib-skeleton-shimmer 1.5s ease-in-out infinite',
    ...userStyle,
  };

  // For multi-line text, render multiple skeletons
  if (variant === 'text' && lines > 1) {
    return (
      <div className={`ib-skeleton-group ${className}`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }} {...rest}>
        {Array.from({ length: lines }).map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              ...baseStyle,
              // Last line is shorter for natural look
              width: i === lines - 1 ? '70%' : (width || defaults.width),
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <span
      className={`ib-skeleton ib-skeleton--${variant} ${className}`}
      aria-hidden="true"
      role="presentation"
      style={baseStyle}
      {...rest}
    />
  );
}

export default Skeleton;
